import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { authReturnLocation } from '../../auth/authReturn';
import ContentReportDialog from '../social-post/ContentReportDialog';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  appendUniqueComments,
  canAuthorMutateComment,
  commentDeleteRequest,
  commentEditRequest,
  commentRequest,
  normalizeComment,
  normalizeCommentPage,
  replaceCommentById,
  socialCommentErrorKey,
} from '../social-post/socialPostDetailModel';
import {
  COMMENT_CONTEXT_PAGE_LIMIT,
  commentDomId,
  searchTargetAcrossPages,
} from './commentContextNavigation';
import { useTravelText } from '../../app/TravelLocaleContext';
import '../social-post/social-post.css';

const C = 'travel.comments';

/** 依 app locale 顯示日期；時區沿用既有（由 Intl 依裝置時區）。 */
function timeLabel(value, intlLocale) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(intlLocale, { dateStyle: 'medium' }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
  }
}

function CommentAuthor({ comment, summaries }) {
  const { tf } = useTravelText();
  const displayName = memberDisplayName(comment.authorMemberId, summaries);
  return (
    <Link to={`/social/creators/${comment.authorMemberId}`} aria-label={tf(`${C}.author`, { name: displayName })}>
      <span className="comment__author-name">{displayName}</span>
    </Link>
  );
}

function CommentItem({
  comment,
  targetId,
  commentApi,
  summaries,
  currentMemberId,
  isAuthenticated,
  reply = false,
  onReplace,
  onDeleted,
  onReplyAuthors,
  focusTarget = false,
  seekReplyId = null,
  onReplySeekResult,
}) {
  const { t, tf, tp, intlLocale } = useTravelText();
  const navigate = useNavigate();
  const location = useLocation();
  const canMutate = canAuthorMutateComment(comment, currentMemberId);
  const canReport = !canMutate && !comment.deleted && isAuthenticated;
  const replyTargetName = comment.replyToAuthorName
    || (comment.replyToAuthorMemberId
      ? memberDisplayName(comment.replyToAuthorMemberId, summaries)
      : '');
  const directReplyCount = comment.directReplyCount;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.content);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pending, setPending] = useState('');
  const [itemError, setItemError] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const editId = `edit-comment-${comment.commentId}`;
  const articleRef = useRef(null);

  useEffect(() => {
    if (!focusTarget || !articleRef.current) return undefined;
    const node = articleRef.current;
    if (typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
    if (typeof node.focus === 'function') {
      node.focus({ preventScroll: true });
    }
    return undefined;
  }, [focusTarget, comment.commentId]);

  useEffect(() => {
    if (!editing) setDraft(comment.content);
  }, [comment.content, editing]);

  async function saveEdit(event) {
    event.preventDefault();
    if (pending || !draft.trim()) return;
    setPending('edit');
    setItemError('');
    try {
      const result = await commentApi.editComment(
        targetId,
        comment.commentId,
        commentEditRequest(draft, comment.rowVersion),
      );
      if (result?.comment) onReplace(normalizeComment(result.comment));
      setEditing(false);
    } catch (cause) {
      setItemError(socialCommentErrorKey(cause));
    } finally {
      setPending('');
    }
  }

  async function confirmRemove() {
    if (pending) return;
    const wasActive = !comment.deleted;
    setPending('delete');
    setItemError('');
    try {
      const result = await commentApi.deleteComment(
        targetId,
        comment.commentId,
        commentDeleteRequest(comment.rowVersion),
      );
      const next = result?.comment ? normalizeComment(result.comment) : comment;
      onReplace(next);
      setConfirmDelete(false);
      if (wasActive && next.deleted) onDeleted?.();
    } catch (cause) {
      setItemError(socialCommentErrorKey(cause));
    } finally {
      setPending('');
    }
  }

  function openReport() {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: authReturnLocation(location) } });
      return;
    }
    setReportOpen(true);
  }

  return (
    <article
      ref={articleRef}
      id={commentDomId(comment.commentId)}
      data-comment-id={comment.commentId}
      className={`comment${reply ? ' comment--reply' : ''}${comment.deleted ? ' comment--deleted' : ''}`}
      tabIndex={focusTarget ? -1 : undefined}
      aria-current={focusTarget ? 'true' : undefined}
    >
      <header>
        <CommentAuthor comment={comment} summaries={summaries} />
        <time dateTime={comment.createdAt || undefined}>{timeLabel(comment.createdAt, intlLocale)}</time>
      </header>
      {editing ? (
        <form className="comment-form comment-form--edit" onSubmit={saveEdit}>
          <label htmlFor={editId}>{t(`${C}.editLabel`)}</label>
          <textarea
            id={editId}
            value={draft}
            maxLength={4000}
            disabled={Boolean(pending)}
            onChange={(event) => setDraft(event.target.value)}
          />
          <div className="comment__actions">
            <button type="submit" disabled={Boolean(pending) || !draft.trim()}>
              {t(pending === 'edit' ? `${C}.saving` : `${C}.save`)}
            </button>
            <button
              type="button"
              disabled={Boolean(pending)}
              onClick={() => { setEditing(false); setDraft(comment.content); setItemError(''); }}
            >
              {t(`${C}.cancel`)}
            </button>
          </div>
        </form>
      ) : (
        <>
          {reply && replyTargetName ? (
            <p className="comment__reply-target">
              <span className="comment__reply-at">@{replyTargetName.replace(/^@/, '')}</span>
            </p>
          ) : null}
          <p className="comment__bubble">
            {comment.deleted ? t(`${C}.deleted`) : comment.content}
            {!comment.deleted && directReplyCount ? (
              <span className="comment__direct-replies">{tp(`${C}.replyCount`, directReplyCount)}</span>
            ) : null}
          </p>
        </>
      )}
      {comment.edited && !comment.deleted && !editing ? <small>{t(`${C}.edited`)}</small> : null}
      {itemError ? <p className="social-detail__inline-error" role="alert" aria-live="assertive">{t(itemError)}</p> : null}
      {!editing ? (
        <div className="comment__actions">
          {canMutate ? (
            <button
              type="button"
              disabled={Boolean(pending)}
              aria-label={tf(`${C}.editAria`, { id: comment.commentId })}
              onClick={() => { setConfirmDelete(false); setEditing(true); setDraft(comment.content); setItemError(''); }}
            >
              {t(`${C}.edit`)}
            </button>
          ) : null}
          {canMutate ? (
            confirmDelete ? (
              <>
                <button
                  type="button"
                  className="comment__danger"
                  disabled={Boolean(pending)}
                  aria-label={tf(`${C}.confirmDeleteAria`, { id: comment.commentId })}
                  onClick={confirmRemove}
                >
                  {t(pending === 'delete' ? `${C}.deleting` : `${C}.confirmDelete`)}
                </button>
                <button type="button" disabled={Boolean(pending)} onClick={() => setConfirmDelete(false)}>{t(`${C}.cancel`)}</button>
              </>
            ) : (
              <button
                type="button"
                className="comment__danger"
                disabled={Boolean(pending)}
                aria-label={tf(`${C}.deleteAria`, { id: comment.commentId })}
                onClick={() => setConfirmDelete(true)}
              >
                {t(`${C}.delete`)}
              </button>
            )
          ) : null}
          {canReport ? (
            <button
              type="button"
              data-testid={`comment-report-${comment.commentId}`}
              disabled={Boolean(pending)}
              aria-label={tf(`${C}.reportAria`, { id: comment.commentId })}
              onClick={openReport}
            >
              {t(`${C}.report`)}
            </button>
          ) : null}
        </div>
      ) : null}
      {confirmDelete ? (
        <p className="comment__confirm" role="status">{t(`${C}.confirmDeleteBody`)}</p>
      ) : null}
      {reportOpen ? (
        <ContentReportDialog
          open
          targetType="COMMENT"
          targetId={comment.commentId}
          titleKey="travel.safety.reportContent"
          onClose={() => setReportOpen(false)}
        />
      ) : null}
      {!reply ? (
        <ReplyThread
          targetId={targetId}
          commentId={comment.commentId}
          commentApi={commentApi}
          summaries={summaries}
          currentMemberId={currentMemberId}
          isAuthenticated={isAuthenticated}
          onDeleted={onDeleted}
          onReplyAuthors={onReplyAuthors}
          seekReplyId={seekReplyId}
          focusId={seekReplyId}
          onSeekResult={onReplySeekResult}
        />
      ) : null}
    </article>
  );
}

function ReplyThread({
  targetId,
  commentId,
  commentApi,
  summaries,
  currentMemberId,
  isAuthenticated,
  onDeleted,
  onReplyAuthors,
  seekReplyId = null,
  focusId = null,
  onSeekResult,
}) {
  const { t } = useTravelText();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [replies, setReplies] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('idle');
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const generation = useRef(0);

  useEffect(() => () => { generation.current += 1; }, []);

  useEffect(() => {
    if (!seekReplyId) return undefined;
    let cancelled = false;
    const requestGeneration = ++generation.current;
    (async () => {
      setOpen(true);
      setPhase('loading');
      setError('');
      try {
        const first = normalizeCommentPage(await commentApi.listReplies(targetId, commentId, {
          limit: COMMENT_CONTEXT_PAGE_LIMIT,
        }));
        if (cancelled || requestGeneration !== generation.current) return;
        const searched = await searchTargetAcrossPages({
          targetId: seekReplyId,
          initialItems: first.items,
          initialCursor: first.cursor,
          initialHasMore: first.hasMore,
          loadPage: async (cursor) => normalizeCommentPage(await commentApi.listReplies(targetId, commentId, {
            ...cursor,
            limit: COMMENT_CONTEXT_PAGE_LIMIT,
          })),
        });
        if (cancelled || requestGeneration !== generation.current) return;
        setReplies(searched.items);
        setCursor(searched.cursor);
        setHasMore(searched.hasMore);
        setPhase('ready');
        if (searched.status === 'TARGET_FOUND') {
          onSeekResult?.('TARGET_FOUND');
        } else if (searched.status === 'REQUEST_FAILED') {
          setError(socialCommentErrorKey(searched.error));
          onSeekResult?.('REQUEST_FAILED');
        } else {
          onSeekResult?.('REPLY_NOT_FOUND');
        }
      } catch (cause) {
        if (cancelled || requestGeneration !== generation.current) return;
        setError(socialCommentErrorKey(cause));
        setPhase('error');
        onSeekResult?.('REQUEST_FAILED');
      }
    })();
    return () => { cancelled = true; generation.current += 1; };
  }, [seekReplyId, targetId, commentId, commentApi, onSeekResult]);

  useEffect(() => {
    onReplyAuthors?.(commentId, replies.map((item) => item.authorMemberId));
  }, [commentId, replies, onReplyAuthors]);

  async function load(append = false) {
    const requestGeneration = ++generation.current;
    setOpen(true);
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const page = normalizeCommentPage(await commentApi.listReplies(targetId, commentId, {
        ...(append ? cursor : {}),
        limit: 20,
      }));
      if (requestGeneration !== generation.current) return;
      setReplies((current) => (append ? appendUniqueComments(current, page.items) : page.items));
      setCursor(page.cursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration === generation.current) {
        setError(socialCommentErrorKey(cause));
        setPhase('error');
      }
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (!isAuthenticated) { navigate('/login', { state: { from: authReturnLocation(location) } }); return; }
    if (!text.trim() || phase === 'submitting') return;
    setPhase('submitting');
    setError('');
    try {
      const result = await commentApi.createReply(targetId, commentId, commentRequest(text));
      if (result?.comment) setReplies((current) => appendUniqueComments(current, [normalizeComment(result.comment)]));
      setText('');
      setPhase('ready');
    } catch (cause) {
      setError(socialCommentErrorKey(cause));
      setPhase('error');
    }
  }

  if (!open) return <button className="comment__reply-toggle" type="button" onClick={() => load()}>{t(`${C}.viewReplies`)}</button>;

  return (
    <div className="comment__replies">
      {phase === 'loading' ? <p aria-live="polite">Loading replies…</p> : null}
      {phase !== 'loading' && replies.length === 0 ? <p>No replies yet.</p> : null}
      <ol className="comments__thread">
        {replies.map((item) => (
          <li key={item.commentId}>
            <CommentItem
              comment={item}
              targetId={targetId}
              commentApi={commentApi}
              summaries={summaries}
              currentMemberId={currentMemberId}
              isAuthenticated={isAuthenticated}
              reply
              focusTarget={focusId != null && String(item.commentId) === String(focusId)}
              onReplace={(next) => setReplies((current) => replaceCommentById(current, next))}
              onDeleted={onDeleted}
            />
          </li>
        ))}
      </ol>
      {error ? <p className="social-detail__inline-error" role="alert">{t(error)}</p> : null}
      {hasMore ? (
        <button className="comments__more comments__more--replies" type="button" disabled={phase === 'loading-more'} onClick={() => load(true)}>
          {phase === 'loading-more' ? 'Loading…' : 'Load more replies'}
        </button>
      ) : null}
      <form className="comment-form comment-form--reply" onSubmit={submit}>
        <label htmlFor={`reply-${commentId}`}>{t(`${C}.replyLabel`)}</label>
        <textarea id={`reply-${commentId}`} value={text} maxLength={4000} onChange={(event) => setText(event.target.value)} placeholder={t(`${C}.replyPlaceholder`)} />
        <button type="submit" disabled={phase === 'submitting' || !text.trim()}>{t(phase === 'submitting' ? `${C}.posting` : `${C}.replyPost`)}</button>
      </form>
    </div>
  );
}

export default function TargetComments({
  targetId,
  commentApi,
  title,
  emptyHint,
  composeLabel,
  onCommentCreated,
  onCommentDeleted,
  commentContext = null,
}) {
  const { t } = useTravelText();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const currentMemberId = user?.memberId || user?.id || '';
  const [comments, setComments] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [text, setText] = useState('');
  const [replyAuthorIds, setReplyAuthorIds] = useState({});
  const [contextState, setContextState] = useState('NO_CONTEXT');
  const [focusId, setFocusId] = useState(null);
  const [activeParentId, setActiveParentId] = useState(null);
  const generation = useRef(0);
  const parentSearchKey = useRef('');
  const context = commentContext && commentContext.state
    ? commentContext
    : { state: 'NO_CONTEXT', commentId: null, replyId: null };
  const authorIds = useMemo(
    () => [
      ...comments.map((item) => item.authorMemberId),
      ...Object.values(replyAuthorIds).flat(),
    ],
    [comments, replyAuthorIds],
  );
  const summaries = useMemberDisplayNames(authorIds);

  function rememberReplyAuthors(parentId, ids) {
    setReplyAuthorIds((current) => {
      const next = Array.isArray(ids) ? ids : [];
      const previous = current[parentId] || [];
      if (previous.length === next.length && previous.every((id, index) => String(id) === String(next[index]))) {
        return current;
      }
      return { ...current, [parentId]: next };
    });
  }

  async function load(append = false) {
    const requestGeneration = generation.current;
    setPhase(append ? 'loading-more' : 'loading');
    setError('');
    try {
      const page = normalizeCommentPage(await commentApi.listComments(targetId, {
        ...(append ? cursor : {}),
        limit: COMMENT_CONTEXT_PAGE_LIMIT,
      }));
      if (requestGeneration !== generation.current) return;
      setComments((current) => (append ? appendUniqueComments(current, page.items) : page.items));
      setCursor(page.cursor);
      setHasMore(page.hasMore);
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration === generation.current) {
        setError(socialCommentErrorKey(cause));
        setPhase('error');
      }
    }
  }

  const handleReplySeekResult = useCallback((result) => {
    if (result === 'TARGET_FOUND') {
      setContextState('TARGET_FOUND');
      setFocusId(context.replyId);
      return;
    }
    if (result === 'REQUEST_FAILED') {
      setContextState('REQUEST_FAILED');
      return;
    }
    setContextState('PARENT_FOUND_REPLY_NOT_FOUND');
    setFocusId(context.commentId);
  }, [context.commentId, context.replyId]);

  useEffect(() => {
    generation.current += 1;
    parentSearchKey.current = '';
    setComments([]);
    setCursor(null);
    setHasMore(false);
    setText('');
    setReplyAuthorIds({});
    setFocusId(null);
    setActiveParentId(null);
    setContextState(context.state === 'INVALID_CONTEXT' ? 'INVALID_CONTEXT' : 'NO_CONTEXT');
    load();
    return () => { generation.current += 1; };
  }, [targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (context.state !== 'READY') {
      parentSearchKey.current = '';
      setFocusId(null);
      setActiveParentId(null);
      setContextState(context.state === 'INVALID_CONTEXT' ? 'INVALID_CONTEXT' : 'NO_CONTEXT');
      return undefined;
    }
    if (phase !== 'ready') return undefined;
    const key = `${targetId}:${context.commentId}:${context.replyId || ''}`;
    if (parentSearchKey.current === key) return undefined;
    parentSearchKey.current = key;
    let cancelled = false;
    (async () => {
      setContextState('SEARCHING');
      const searched = await searchTargetAcrossPages({
        targetId: context.commentId,
        initialItems: comments,
        initialCursor: cursor,
        initialHasMore: hasMore,
        loadPage: async (nextCursor) => normalizeCommentPage(await commentApi.listComments(targetId, {
          ...nextCursor,
          limit: COMMENT_CONTEXT_PAGE_LIMIT,
        })),
      });
      if (cancelled) return;
      setComments(searched.items);
      setCursor(searched.cursor);
      setHasMore(searched.hasMore);
      if (searched.status === 'TARGET_FOUND') {
        setActiveParentId(context.commentId);
        if (context.replyId) {
          setContextState('SEARCHING');
          setFocusId(null);
        } else {
          setContextState('TARGET_FOUND');
          setFocusId(context.commentId);
        }
        return;
      }
      if (searched.status === 'REQUEST_FAILED') {
        setError(socialCommentErrorKey(searched.error));
        setContextState('REQUEST_FAILED');
        return;
      }
      setContextState('PARENT_NOT_FOUND');
    })();
    return () => {
      cancelled = true;
      if (parentSearchKey.current === key) parentSearchKey.current = '';
    };
  }, [phase, targetId, context.state, context.commentId, context.replyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function submit(event) {
    event.preventDefault();
    if (!isAuthenticated) { navigate('/login', { state: { from: authReturnLocation(location) } }); return; }
    if (!text.trim() || phase === 'submitting') return;
    const requestGeneration = generation.current;
    setPhase('submitting');
    setError('');
    try {
      const result = await commentApi.createComment(targetId, commentRequest(text));
      if (requestGeneration !== generation.current) return;
      if (result?.comment) setComments((current) => [normalizeComment(result.comment), ...current]);
      if (!result?.replayed) onCommentCreated?.();
      setText('');
      setPhase('ready');
    } catch (cause) {
      if (requestGeneration === generation.current) {
        setError(socialCommentErrorKey(cause));
        setPhase('error');
      }
    }
  }

  return (
    <section className="comments" aria-labelledby="comments-title">
      <h2 id="comments-title">{title ?? t(`${C}.title`)}</h2>
      {isAuthenticated ? (
        <form className="comment-form" onSubmit={submit}>
          <label htmlFor="new-comment">{composeLabel ?? t(`${C}.compose`)}</label>
          <textarea id="new-comment" value={text} maxLength={4000} onChange={(event) => setText(event.target.value)} placeholder={t(`${C}.placeholder`)} />
          <button type="submit" disabled={phase === 'submitting' || !text.trim()}>{t(phase === 'submitting' ? `${C}.posting` : `${C}.post`)}</button>
        </form>
      ) : (
        <button className="comments__signin" type="button" onClick={() => navigate('/login', { state: { from: authReturnLocation(location) } })}>{t(`${C}.signIn`)}</button>
      )}
      {contextState === 'SEARCHING' ? (
        <p className="comments__target-status" role="status">{t(`${C}.seeking`)}</p>
      ) : null}
      {contextState === 'TARGET_FOUND' ? (
        <p className="comments__target-status" role="status">{t(`${C}.located`)}</p>
      ) : null}
      {contextState === 'PARENT_NOT_FOUND' || contextState === 'PARENT_FOUND_REPLY_NOT_FOUND' ? (
        <p className="comments__target-status" role="status">{t(`${C}.contextMissing`)}</p>
      ) : null}
      {phase === 'loading' ? <p className="comments__state" aria-live="polite">{t(`${C}.loading`)}</p> : null}
      {phase !== 'loading' && comments.length === 0 && !error ? <p className="comments__state">{emptyHint ?? t(`${C}.empty`)}</p> : null}
      {error ? <div className="comments__error" role="alert">{t(error)} <button type="button" onClick={() => load()}>{t(`${C}.tryAgain`)}</button></div> : null}
      <ol className="comments__list">
        {comments.map((comment) => (
          <li key={comment.commentId}>
            <CommentItem
              comment={comment}
              targetId={targetId}
              commentApi={commentApi}
              summaries={summaries}
              currentMemberId={currentMemberId}
              isAuthenticated={isAuthenticated}
              focusTarget={focusId != null && String(comment.commentId) === String(focusId)}
              seekReplyId={
                activeParentId
                && String(comment.commentId) === String(activeParentId)
                && context.replyId
                  ? context.replyId
                  : null
              }
              onReplySeekResult={handleReplySeekResult}
              onReplace={(next) => setComments((current) => replaceCommentById(current, next))}
              onDeleted={onCommentDeleted}
              onReplyAuthors={rememberReplyAuthors}
            />
          </li>
        ))}
      </ol>
      {hasMore ? (
        <button className="comments__more" type="button" disabled={phase === 'loading-more'} onClick={() => load(true)}>
          {phase === 'loading-more' ? 'Loading…' : 'Load more comments'}
        </button>
      ) : null}
    </section>
  );
}
